import React from 'react';
import { useTranslation } from 'react-i18next';

import { IDatepickerProps, IDatePresetSelectValues } from '@private/datepicker';

import { DATE_PRESET_BUTTONS } from 'components/Datepicker/constants';
import { ICONS_MAP } from 'constants/icons';
import { appReactMemo } from 'hocs';
import { useDateLocale } from 'hooks/useDateLocale';
import { getPreviousMonthDate } from 'utils/common';

import { StyledDatePicker } from './styled';
import { timezoneOptions } from './timezones';

export type IDatepicker = Omit<
  IDatepickerProps,
  'locale' | 'onClose' | 'onOpen' | 'open' | 'timezonesList' | 'translationConfig'
> & {
  hideTimezoneSelect?: boolean;
  isAutoFocused?: boolean;
  isBackground?: boolean;
  onClose?: () => void;
};

const NO_TIMEZONES: typeof timezoneOptions = [];

const Datepicker = ({
  config = {},
  datePresetButtonsValues: initialDatePresetButtons = DATE_PRESET_BUTTONS,
  datePresetSelectValues: initialDatePresetSelect,
  disableTooltip,
  hideTimezoneSelect,
  isAutoFocused,
  isBackground = true,
  onClose: externalOnClose,
  settingsConfig = {},
  variant = 'double-range-picker',
  ...restProps
}: IDatepicker) => {
  const { t } = useTranslation('translation', { keyPrefix: 'common.datepicker' });

  const [isOpen, setIsOpen] = React.useState(false);

  const locale = useDateLocale();

  const onOpen = React.useCallback(() => {
    setIsOpen(true);
  }, []);

  const onClose = React.useCallback(() => {
    setIsOpen(false);
    externalOnClose?.();
  }, [externalOnClose]);

  React.useEffect(() => {
    if (isAutoFocused) {
      onOpen();
    }
  }, [isAutoFocused, onOpen]);

  const calendarConfig = React.useMemo(
    () => ({
      calendarIcon: <ICONS_MAP.Calendar width={18} />,
      datePresetIcon: <ICONS_MAP.CalendarPreset />,
      initialVisibleMonth:
        variant === 'double-range-picker' ? getPreviousMonthDate() : undefined,
      rangeDefaultEndOfDay: true,
      ...config,
    }),
    [config, variant],
  );

  const translationConfig = React.useMemo(
    () => ({
      applyButtonText: t('applyButtonText'),
      cancelButtonText: t('cancelButtonText'),
      customSuggestion: t('customSuggestion'),
      datePresetInputPlaceholder: t('datePresetInputPlaceholder'),
      datePresetSelectPlaceholder: t('datePresetSelectPlaceholder'),
      datePresetTitle: t('datePresetTitle'),
      dayLabel: t('dayLabel'),
      // dayLabel/daysLabel are a two-form plural and stay only as a fallback;
      // daysCountFormatter lets i18next pick the right Ukrainian form.
      daysCountFormatter: (daysCount: number) => t('daysCount', { count: daysCount }),
      daysLabel: t('daysLabel'),
      hourMaskLabel: t('hourMaskLabel'),
      hourTimeLabel: t('hourTimeLabel'),
      minuteMaskLabel: t('minuteMaskLabel'),
      minutesTimeLabel: t('minutesTimeLabel'),
      secondMaskLabel: t('secondMaskLabel'),
      secondsTimeLabel: t('secondsTimeLabel'),
      suggestionsTitle: t('suggestionsTitle'),
      timeLabel: t('timeLabel'),
      timezonesPlaceholder: t('timezonesPlaceholder'),
    }),
    [t],
  );

  const datePresetButtonsValues = React.useMemo(
    () =>
      initialDatePresetButtons.map((item) => ({
        ...item,
        label: t(item.label as never),
      })),
    [initialDatePresetButtons, t],
  );

  const datePresetSelectValues = React.useMemo(() => {
    if (initialDatePresetSelect) {
      return initialDatePresetSelect.map((item) => ({
        ...item,
        label: t(item.label as never),
      }));
    }
    const timePreset = {
      label: t('presets.previousHours'),
      type: 'hours',
      value: 2,
      variant: 'prev',
    } as IDatePresetSelectValues;
    const basePresets = [
      { label: t('presets.previousDays'), type: 'days', value: 4, variant: 'prev' },
      { label: t('presets.previousWeeks'), type: 'weeks', value: 6, variant: 'prev' },
      { label: t('presets.previousMonths'), type: 'months', value: 8, variant: 'prev' },
      { label: t('presets.previousYears'), type: 'years', value: 10, variant: 'prev' },
    ] as IDatePresetSelectValues[];

    return settingsConfig?.hideTime ? basePresets : [timePreset, ...basePresets];
  }, [initialDatePresetSelect, settingsConfig?.hideTime, t]);

  const localSettingsConfig = React.useMemo(
    () => ({
      datepickerMenuInPopover: false,
      hideDefaultEnabledTime: true,
      hideInputs: true,
      switchedAndZoneTogether: true,
      timepickerInPopovers: false,
      timezoneOnTop: true,
      ...settingsConfig,
    }),
    [settingsConfig],
  );

  return (
    <StyledDatePicker
      {...restProps}
      $isBackground={isBackground}
      config={calendarConfig}
      datePresetButtonsValues={datePresetButtonsValues}
      datePresetSelectValues={datePresetSelectValues}
      disableTooltip={disableTooltip as never}
      locale={locale}
      onClose={onClose}
      onOpen={onOpen}
      open={isOpen}
      settingsConfig={localSettingsConfig}
      timezonesList={hideTimezoneSelect ? NO_TIMEZONES : timezoneOptions}
      translationConfig={translationConfig}
      variant={variant}
    />
  );
};

export default appReactMemo(Datepicker);
